import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../components/SideBar/Sidebar';
import * as Body from './Styles';
import AddIcon from '../../../assets/icons/add-icon.svg';
import DeleteIcon from '../../../assets/icons/delete-red-material-icon.svg';
import SearchIcon from '../../../assets/icons/Search.svg';
import Avatar from '../../../assets/images/avatar.png';
import * as Functions from '../../../utils/functions/Functions';
import * as defines from '../../../utils/defines/Defines';
import Input from '../../components/Inputs/Input/Input';
import Button from '../../components/Buttons/Button/Button';
import DeleteUser from './Modals/DeleteUser';
import SendInvite from './Modals/SendInvite';
import UpdateCargo from './Modals/UpdateCargo';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import { State } from '../../redux/store';
import ButtonWrapper from '../../components/Buttons/ButtonWrapper/ButtonWrapper';
import Loading from '../../components/Loading/Loading';

type UsuarioProps = {
  idEmpresarial: string;
  idUsuario: string;
  fotoUsuario: string | null;
  ddd: string;
  celular: string;
  nomeCompleto: string;
  email: string;
  cargo: number;
  encarregadoDados: number;
};

const DefaultManageUsers = () => {
  // Redux state
  const { acessLevel, userType, selectedUnity } = useSelector((state: State) => ({
    acessLevel: state.acessLevel.acessLevel,
    userType: state.userType.userType,
    selectedUnity: state.selectedUnity.selectedUnity
  }));
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [modalStates, setModalStates] = useState({
    createUser: false,
    delete: false,
    changeCargo: false
  });
  const [users, setUsers] = useState({
    all: [] as UsuarioProps[],
    filtered: [] as UsuarioProps[],
    selected: null as UsuarioProps | null
  });
  const [filialInfo, setFilialInfo] = useState({
    nomeFilial: ''
  });
  const searchRef = useRef<HTMLInputElement>(null);

  // Constants
  const isAdmin = acessLevel === defines.CargoEmpresarial['Administrador'];
  const hasSelectedUser = !!users.selected;

  // Helper functions
  const getCargoLabel = (cargo: number) => {
      switch(cargo){
        case defines.CargoEmpresarial.Administrador:
          return 'Administrador'
        case defines.CargoEmpresarial.Padrão:
          return 'Padrão'
        default:
          return 'Desconhecido'
      }
    };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${defines.apiURL}/api/EmpresarialCRUD?userTypeRequest=${userType}&idFilial=${Functions.ReplaceSpecialCharacters(selectedUnity)}`,
        { withCredentials: true }
      );
      const sorted = Functions.SortResponse(response.data.content, 'nomeCompleto');
      setUsers(prev => ({ ...prev, all: sorted, filtered: sorted }));
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilialInfo = async () => {
    try {
      const response = await axios.get(
        `${defines.apiURL}/api/Home?userTypeRequest=${userType}&filialInfo=${Functions.ReplaceSpecialCharacters(selectedUnity)}`,
        { withCredentials: true }
      );
      setFilialInfo({ nomeFilial: response.data.content.nomeFilial });
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleApiError = (error: any) => {
    Functions.ResolveErrors(
      error.response?.status,
      error.response?.data?.code,
      error.response?.data?.message,
      navigate,
      dispatch,
      error.response?.data?.content
    );
  };

  const filterUsers = (name: string) => {
    const filtered = name.length === 0 
      ? users.all 
      : users.all.filter(item => item.nomeCompleto.toUpperCase().includes(name.toUpperCase()));
    setUsers(prev => ({ ...prev, filtered }));
  };

  const selectUser = async (id: string) => {
    if (users.selected?.idUsuario === id) {
      setUsers(prev => ({ ...prev, selected: null }));
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${defines.apiURL}/api/EmpresarialCRUD?id=${Functions.ReplaceSpecialCharacters(id)}&userTypeRequest=${userType}&idFilial=${Functions.ReplaceSpecialCharacters(selectedUnity)}`,
        { withCredentials: true }
      );
      setUsers(prev => ({ ...prev, selected: response.data.content[0] }));
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    if (acessLevel !== defines.CargoEmpresarial['Administrador']) {
      navigate('/permissao');
    }
    fetchUsers();
    fetchFilialInfo();
  }, [selectedUnity]);

  // Render functions
  const renderUserList = () => (
    <Body.Scroll style={{ maxHeight: 'calc(100% - 120px)' }}>
      {users.filtered.length > 0 ? (
        users.filtered.map((user, index) => (
          <Body.Row key={index} style={{ alignItems: 'center' }}>
            <Body.SelectUser onClick={() => selectUser(user.idUsuario)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Body.Photo src={user.fotoUsuario || Avatar} />
                <Body.Text>{user.nomeCompleto}</Body.Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Body.NoteText>{getCargoLabel(user.cargo)}</Body.NoteText>
                {isAdmin && (
                  <ButtonWrapper 
                    style={{ marginLeft: '10px' }}
                    action={() => setModalStates(prev => ({ ...prev, delete: true }))}
                  >
                    <Body.DeleteIcon src={DeleteIcon} />
                  </ButtonWrapper>
                )}
              </div>
            </Body.SelectUser>
          </Body.Row>
        ))
      ) : (
        <Body.Text>Não existem usuários para mostrar.</Body.Text>
      )}
    </Body.Scroll>
  );

  const renderUserDetails = () => (
    <Body.HalfContent style={{ alignItems: 'center', width: '100px' }}>
      <Body.Photo 
        src={users.selected?.fotoUsuario || Avatar} 
        style={{ width: '120px', borderRadius: '60px', height: '120px' }} 
      />
      <Body.Scroll style={{ height: '100%', marginBottom: '10px', maxHeight: 'calc(100vh - 80px)' }}>
        <Input 
          text={users.selected?.nomeCompleto || ''}
		  setText={() => {}}
          title="Nome Completo"
          disabled
        />
        <Input 
          text={users.selected?.email || ''}
		  setText={() => {}}
          title="Email"
          disabled
        />
        <Body.Row>
          <Input 
            text={users.selected?.ddd || ''}
		  	setText={() => {}}
            title="DDD"
            limit={2}
            disabled
          />
          <Input 
            text={users.selected?.celular || ''}
			setText={() => {}}
            title="Telefone"
            disabled
          />
        </Body.Row>
        {isAdmin && (
          <Body.Row>
            <Button 
              text="Alterar Cargo" 
              action={() => setModalStates(prev => ({ ...prev, changeCargo: true }))} 
            />
          </Body.Row>
        )}
      </Body.Scroll>
    </Body.HalfContent>
  );

  return (
    <Body.Container>
      <Sidebar type={userType} content={defines.ContentSidebar.MANAGE_USERS} />
      <Body.Content style={{ flexDirection: 'row' }}>
        <Body.HalfContent style={hasSelectedUser ? { width: 'calc(100% - 120px)' } : { width: '100%' }}>
          <Body.Row style={{ justifyContent: 'space-between', alignItems: 'center', height: '140px' }}>
            <Breadcrumb />
            <Body.Row style={{ alignItems: 'center' }}>
              <Body.FindContent>
                <Body.Find 
                  onChange={(e) => filterUsers(e.target.value)}
                  ref={searchRef}
                />
                <Body.Icon 
                  src={SearchIcon} 
                  style={{ cursor: 'text' }}
                  onClick={() => searchRef.current?.focus()}
                />
              </Body.FindContent>
              {isAdmin && (
                <Body.HeaderButton onClick={() => setModalStates(prev => ({ ...prev, createUser: true }))}>
                  <Body.Icon src={AddIcon} />
                </Body.HeaderButton>
              )}
            </Body.Row>
          </Body.Row>
          {renderUserList()}
        </Body.HalfContent>
        {hasSelectedUser && renderUserDetails()}
      </Body.Content>

      {loading && <Loading />}
      {modalStates.createUser && (
        <SendInvite
          toggle={() => setModalStates(prev => ({ ...prev, createUser: false }))}
          nomeFilial={filialInfo.nomeFilial || 'atual'}
          idFilial={selectedUnity}
        />
      )}
      {modalStates.delete && users.selected && (
        <DeleteUser
          toggle={() => setModalStates(prev => ({ ...prev, delete: false }))}
          usuario={users.selected}
          nomeFilial={filialInfo.nomeFilial || 'atual'}
          idFilial={selectedUnity}
          regenerate={fetchUsers}
        />
      )}
      {modalStates.changeCargo && users.selected && (
        <UpdateCargo
          toggle={() => setModalStates(prev => ({ ...prev, changeCargo: false }))}
          usuario={users.selected}
          idFilial={selectedUnity}
          regenerate={fetchUsers}
        />
      )}
    </Body.Container>
  );
};

export default DefaultManageUsers;